// src/components/AccountantSalaryDashboard.js
import React, { useState, useEffect } from 'react';
import {
  Box,
  Grid,
  Paper,
  Typography,
  Card,
  CardContent,
  Chip,
  IconButton,
  TextField,
  InputAdornment,
  FormControl,
  InputLabel,
  Select,
  MenuItem,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Button,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  Tooltip,
  Avatar,
  alpha,
  useMediaQuery,
  useTheme,
  Stack,
  Alert,
  Snackbar,
  CircularProgress,
  Fade,
  Grow,
  Zoom,
  TablePagination,
  Badge,
  Divider,
  Container,
  LinearProgress,
} from '@mui/material';
import {
  Search as SearchIcon,
  Refresh as RefreshIcon,
  CheckCircle as CheckCircleIcon,
  Cancel as CancelIcon,
  Pending as PendingIcon,
  Assessment as AssessmentIcon,
  Person as PersonIcon,
  AttachMoney as MoneyIcon,
  Download as DownloadIcon,
  Print as PrintIcon,
  Visibility as VisibilityIcon,
  History as HistoryIcon,
  TrendingUp as TrendingUpIcon,
  TrendingDown as TrendingDownIcon,
  Receipt as ReceiptIcon,
  Dashboard as DashboardIcon,
  PeopleAlt as PeopleIcon,
  Savings as SavingsIcon,
  TrendingUp as TrendingIcon,
  Close as CloseIcon,
  BarChart as BarChartIcon,      // ADD THIS LINE
  PieChart as PieChartIcon,
} from '@mui/icons-material';
import {
  BarChart,
  Bar,
  XAxis,
  YAxis,
  CartesianGrid,
  Tooltip as RechartsTooltip,
  ResponsiveContainer,
  PieChart,
  Pie,
  Cell,
  Legend,
  AreaChart,
  Area,
  LineChart,
  Line,
} from 'recharts';


import Sidebar from '../../../../components/sidebars/Sidebar_hotelAccount_cashier';
import TopNav from '../../../../components/TopNav_hotelStaff';


const COLORS = ['#6366f1', '#10b981', '#f59e0b', '#ef4444', '#8b5cf6', '#06b6d4', '#ec4899', '#84cc16'];

const AccountantSalaryDashboard = () => {
  const theme = useTheme();
  const isMobile = useMediaQuery(theme.breakpoints.down('md'));
  const [sidebarOpen, setSidebarOpen] = useState(true);
  const [allSalaries, setAllSalaries] = useState([]);
  const [stats, setStats] = useState(null);
  const [departmentStats, setDepartmentStats] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [searchTerm, setSearchTerm] = useState('');
  const [departmentFilter, setDepartmentFilter] = useState('all');
  const [sortBy, setSortBy] = useState('salary_desc');
  const [selectedStaff, setSelectedStaff] = useState(null);
  const [viewDialogOpen, setViewDialogOpen] = useState(false);
  const [historyDialogOpen, setHistoryDialogOpen] = useState(false);
  const [salaryHistory, setSalaryHistory] = useState([]);
  const [snackbar, setSnackbar] = useState({ open: false, message: '', severity: 'success' });
  
  // Pagination state
  const [page, setPage] = useState(0);
  const [rowsPerPage, setRowsPerPage] = useState(15);

  const toggleSidebar = () => setSidebarOpen((prev) => !prev);

  useEffect(() => {
    fetchAllData();
  }, []);

  const fetchAllData = async () => {
    setLoading(true);
    try {
      const baseUrl = process.env.REACT_APP_API_BASE_URL || '';
      
      const allRes = await fetch(`${baseUrl}/hotel/HR/update_salary.php?type=all`);
      const allResult = await allRes.json();
      if (allResult.success) {
        setAllSalaries(allResult.data || []);
      } else {
        setError(allResult.message);
      }
      
      const statsRes = await fetch(`${baseUrl}/hotel/HR/update_salary.php?type=stats`);
      const statsResult = await statsRes.json();
      if (statsResult.success) {
        setStats(statsResult.stats);
        setDepartmentStats(statsResult.department_stats || []);
      }
      
    } catch (err) {
      setError('Failed to load data');
      setSnackbar({ open: true, message: 'Failed to load salary data', severity: 'error' });
    } finally {
      setLoading(false);
    }
  };

  const fetchSalaryHistory = async (phone) => {
    try {
      const baseUrl = process.env.REACT_APP_API_BASE_URL || '';
      const response = await fetch(`${baseUrl}/hotel/HR/get_employee_info.php?phone=${phone}`);
      const result = await response.json();
      if (result.success && result.data) {
        const pendingAdjustments = result.data.pending_adjustments || [];
        const history = pendingAdjustments.map(adj => ({
          created_at: adj.date_created,
          previous_salary: adj.previous_salary,
          new_salary: adj.new_salary,
          increment_amount: adj.amount,
          reason: adj.reason,
          adjustment_type: adj.adjustment_type,
          status: adj.approval_status
        }));
        setSalaryHistory(history);
      } else {
        setSalaryHistory([]);
      }
    } catch (err) {
      setSalaryHistory([]);
    }
  };

  const handleViewStaff = async (staff) => {
    setSelectedStaff(staff);
    await fetchSalaryHistory(staff.phone);
    setViewDialogOpen(true);
  };

  const handleViewHistory = async (staff) => {
    setSelectedStaff(staff);
    await fetchSalaryHistory(staff.phone);
    setHistoryDialogOpen(true);
  };

  const formatCurrency = (amount) => {
    if (!amount && amount !== 0) return '₦0.00';
    const num = parseFloat(amount);
    if (isNaN(num)) return '₦0.00';
    return new Intl.NumberFormat('en-NG', {
      style: 'currency',
      currency: 'NGN',
      minimumFractionDigits: 2,
      maximumFractionDigits: 2
    }).format(num);
  };

  const formatNumber = (num) => {
    if (!num && num !== 0) return '0';
    const n = Number(num);
    if (isNaN(n)) return '0';
    return new Intl.NumberFormat('en-NG').format(Math.round(n));
  };

  const getFilteredSalaries = () => {
    let filtered = [...allSalaries];
    
    if (searchTerm) {
      const term = searchTerm.toLowerCase();
      filtered = filtered.filter(s => 
        (s.full_name || '').toLowerCase().includes(term) ||
        (s.phone || '').includes(term) ||
        (s.department || '').toLowerCase().includes(term) ||
        (s.email || '').toLowerCase().includes(term)
      );
    }
    
    if (departmentFilter !== 'all') {
      filtered = filtered.filter(s => s.department === departmentFilter);
    }
    
    switch (sortBy) {
      case 'salary_desc':
        filtered.sort((a, b) => (Number(b.salary_amount) || 0) - (Number(a.salary_amount) || 0));
        break;
      case 'salary_asc':
        filtered.sort((a, b) => (Number(a.salary_amount) || 0) - (Number(b.salary_amount) || 0));
        break;
      case 'name_asc':
        filtered.sort((a, b) => (a.full_name || '').localeCompare(b.full_name || ''));
        break;
      case 'name_desc':
        filtered.sort((a, b) => (b.full_name || '').localeCompare(a.full_name || ''));
        break;
      default:
        break;
    }
    
    return filtered;
  };

  const exportToCSV = () => {
    const headers = ['Staff Name', 'Department', 'Phone', 'Email', 'Salary Amount', 'Bank', 'Account Number', 'Account Name'];
    const csvData = getFilteredSalaries().map(s => [
      `"${s.full_name || ''}"`,
      `"${s.department || ''}"`,
      `"${s.phone || ''}"`,
      `"${s.email || ''}"`,
      s.salary_amount || 0,
      `"${s.bank_name || ''}"`,
      `"${s.account_number || ''}"`,
      `"${s.account_name || ''}"`
    ]);
    
    const csvContent = [headers, ...csvData].map(row => row.join(',')).join('\n');
    const blob = new Blob(['\uFEFF' + csvContent], { type: 'text/csv;charset=utf-8;' });
    const url = window.URL.createObjectURL(blob);
    const a = document.createElement('a');
    a.href = url;
    a.download = `payroll_report_${new Date().toISOString().split('T')[0]}.csv`;
    a.click();
    window.URL.revokeObjectURL(url);
    setSnackbar({ open: true, message: 'CSV exported successfully!', severity: 'success' });
  };

  const printReport = () => {
    window.print();
  };

  const filteredSalaries = getFilteredSalaries();
  const paginatedSalaries = filteredSalaries.slice(page * rowsPerPage, page * rowsPerPage + rowsPerPage);
  
  const uniqueDepartments = ['all', ...new Set(allSalaries.map(s => s.department).filter(Boolean))];
  
  const pieChartData = departmentStats.map(dept => ({
    name: dept.department,
    value: Number(dept.total_salary) || 0,
    staffCount: dept.staff_count
  }));

  const totalPayroll = stats?.total_monthly_payroll || 0;
  const averageSalary = stats?.average_salary || 0;
  
  const salaries = allSalaries.map(s => Number(s.salary_amount) || 0).filter(s => s > 0);
  const minSalary = salaries.length > 0 ? Math.min(...salaries) : 0;
  const maxSalary = salaries.length > 0 ? Math.max(...salaries) : 0;

  const salaryBands = [
    { min: 0, max: 50000, label: '₦0 - ₦50k', count: 0, color: '#10b981' },
    { min: 50001, max: 100000, label: '₦50k - ₦100k', count: 0, color: '#3b82f6' },
    { min: 100001, max: 200000, label: '₦100k - ₦200k', count: 0, color: '#f59e0b' },
    { min: 200001, max: 300000, label: '₦200k - ₦300k', count: 0, color: '#8b5cf6' },
    { min: 300001, max: 500000, label: '₦300k - ₦500k', count: 0, color: '#ef4444' },
    { min: 500001, max: Infinity, label: '₦500k+', count: 0, color: '#06b6d4' },
  ];

  allSalaries.forEach(s => {
    const salary = Number(s.salary_amount) || 0;
    const band = salaryBands.find(b => salary >= b.min && salary <= b.max);
    if (band) band.count++;
  });

  const StatCard = ({ title, value, subtitle, trend, trendValue, color, icon: Icon }) => (
    <Grow in timeout={300}>
      <Card sx={{ 
        borderRadius: 4, 
        height: '100%', 
        boxShadow: '0 4px 20px rgba(0,0,0,0.08)',
        transition: 'transform 0.2s, box-shadow 0.2s',
        '&:hover': {
          transform: 'translateY(-4px)',
          boxShadow: '0 8px 30px rgba(0,0,0,0.12)',
        }
      }}>
        <CardContent>
          <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start' }}>
            <Box>
              <Typography variant="body2" sx={{ color: 'text.secondary', fontWeight: 500, letterSpacing: '0.5px' }}>
                {title}
              </Typography>
              <Typography variant="h4" fontWeight="bold" sx={{ mt: 1, fontSize: { xs: '1.75rem', md: '2rem' } }}>
                {value}
              </Typography>
              {subtitle && (
                <Typography variant="caption" sx={{ color: 'text.secondary', mt: 0.5, display: 'block' }}>
                  {subtitle}
                </Typography>
              )}
              {trend && (
                <Chip 
                  size="small" 
                  label={`${trend} ${trendValue}`}
                  icon={trend === '↑' ? <TrendingUpIcon sx={{ fontSize: 14 }} /> : <TrendingDownIcon sx={{ fontSize: 14 }} />}
                  sx={{ mt: 1, bgcolor: alpha(trend === '↑' ? '#10b981' : '#ef4444', 0.1), color: trend === '↑' ? '#10b981' : '#ef4444', fontSize: '0.7rem' }}
                />
              )}
            </Box>
            <Avatar sx={{ bgcolor: alpha(color, 0.1), color: color, width: 52, height: 52, borderRadius: 3 }}>
              <Icon sx={{ fontSize: 28 }} />
            </Avatar>
          </Box>
        </CardContent>
      </Card>
    </Grow>
  );

  if (loading && allSalaries.length === 0) {
    return (
      <Box sx={{ display: 'flex', height: '100vh' }}>
        <Sidebar isOpen={sidebarOpen} toggleSidebar={toggleSidebar} />
        <Box sx={{ flexGrow: 1, p: 3, display: 'flex', alignItems: 'center', justifyContent: 'center', flexDirection: 'column', gap: 2 }}>
          <CircularProgress size={48} sx={{ color: '#6366f1' }} />
          <Typography variant="h6" color="textSecondary">Loading salary data...</Typography>
        </Box>
      </Box>
    );
  }

  return (
    <Box sx={{ display: 'flex', height: '100vh', bgcolor: '#f8fafc' }}>
      <Sidebar isOpen={sidebarOpen} toggleSidebar={toggleSidebar} />

      <Box sx={{ 
        flexGrow: 1, 
        p: { xs: 2, md: 3 }, 
        ml: isMobile ? 0 : sidebarOpen ? 30 : 5, 
        maxWidth: '1600px', 
        mx: 'auto', 
        width: '100%', 
        overflow: 'auto', 
        height: '100vh',
        transition: 'all 0.3s ease',
      }}>
        <TopNav
          onLogout={() => {
            localStorage.removeItem('authToken');
            localStorage.removeItem('userProfile');
            localStorage.removeItem('phoneNumber');
            window.location.href = '/login';
          }}
          onNotificationClick={() => {}}
          toggleSidebar={toggleSidebar}
        />

        {/* Modern Gradient Header */}
        <Paper elevation={0} sx={{ 
          p: { xs: 2, md: 4 }, 
          mb: 4, 
          borderRadius: 4, 
          background: 'linear-gradient(135deg, #4f46e5 0%, #7c3aed 50%, #6366f1 100%)', 
          color: 'white',
          position: 'relative',
          overflow: 'hidden',
        }}>
          <Box sx={{ position: 'relative', zIndex: 2 }}>
            <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', flexWrap: 'wrap', gap: 2 }}>
              <Box>
                <Typography variant="h4" fontWeight="bold" sx={{ mb: 0.5, fontSize: { xs: '1.5rem', md: '2rem' } }}>
                  Accountant Dashboard
                </Typography>
                <Typography variant="body2" sx={{ opacity: 0.9, mb: 1 }}>
                  Financial overview, payroll management, and salary analytics
                </Typography>
                <Chip 
                  label={`${allSalaries.length} Active Staff Members`}
                  size="small"
                  sx={{ bgcolor: 'rgba(255,255,255,0.2)', color: 'white', fontWeight: 500 }}
                />
              </Box>
              <Box sx={{ display: 'flex', gap: 1.5, flexWrap: 'wrap' }}>
                <Button 
                  variant="contained" 
                  startIcon={<PrintIcon />} 
                  onClick={printReport} 
                  sx={{ bgcolor: 'white', color: '#4f46e5', '&:hover': { bgcolor: '#f8fafc' }, borderRadius: 2, textTransform: 'none' }}
                >
                  Print
                </Button>
                <Button 
                  variant="contained" 
                  startIcon={<DownloadIcon />} 
                  onClick={exportToCSV} 
                  sx={{ bgcolor: 'white', color: '#4f46e5', '&:hover': { bgcolor: '#f8fafc' }, borderRadius: 2, textTransform: 'none' }}
                >
                  Export CSV
                </Button>
                <Button 
                  variant="contained" 
                  startIcon={<RefreshIcon />} 
                  onClick={fetchAllData} 
                  sx={{ bgcolor: 'white', color: '#4f46e5', '&:hover': { bgcolor: '#f8fafc' }, borderRadius: 2, textTransform: 'none' }}
                >
                  Refresh
                </Button>
              </Box>
            </Box>
          </Box>
          {/* Decorative elements */}
          <Box sx={{ position: 'absolute', top: -50, right: -50, width: 200, height: 200, borderRadius: '50%', bgcolor: 'rgba(255,255,255,0.1)', zIndex: 1 }} />
          <Box sx={{ position: 'absolute', bottom: -30, left: -30, width: 150, height: 150, borderRadius: '50%', bgcolor: 'rgba(255,255,255,0.08)', zIndex: 1 }} />
        </Paper>

        {/* Statistics Cards Grid */}
        <Grid container spacing={3} sx={{ mb: 4 }}>
          <Grid item xs={12} sm={6} md={3}>
            <StatCard 
              title="Total Monthly Payroll" 
              value={formatCurrency(totalPayroll)} 
              subtitle="Across all departments" 
              color="#10b981" 
              icon={MoneyIcon}
              trend="↑"
              trendValue="12% vs last month"
            />
          </Grid>
          <Grid item xs={12} sm={6} md={3}>
            <StatCard 
              title="Annual Payroll" 
              value={formatCurrency(totalPayroll * 12)} 
              subtitle="Yearly projection" 
              color="#3b82f6" 
              icon={ReceiptIcon}
            />
          </Grid>
          <Grid item xs={12} sm={6} md={3}>
            <StatCard 
              title="Average Salary" 
              value={formatCurrency(averageSalary)} 
              subtitle="Per employee" 
              color="#f59e0b" 
              icon={AssessmentIcon}
            />
          </Grid>
          <Grid item xs={12} sm={6} md={3}>
            <StatCard 
              title="Total Staff" 
              value={formatNumber(allSalaries.length)} 
              subtitle="Active employees" 
              color="#8b5cf6" 
              icon={PeopleIcon}
            />
          </Grid>
        </Grid>

        {/* Salary Range Cards */}
        <Grid container spacing={3} sx={{ mb: 4 }}>
          <Grid item xs={12} sm={6} md={4}>
            <Zoom in timeout={400}>
              <Card sx={{ borderRadius: 3, bgcolor: 'linear-gradient(135deg, #e8f5e9 0%, #c8e6c9 100%)', borderLeft: '4px solid #10b981' }}>
                <CardContent>
                  <Typography variant="body2" sx={{ color: '#065f46', fontWeight: 500 }}>Highest Salary</Typography>
                  <Typography variant="h5" fontWeight="bold" sx={{ color: '#10b981', mt: 0.5 }}>{formatCurrency(maxSalary)}</Typography>
                  <Typography variant="caption" sx={{ color: '#065f46' }}>🏆 Top earner</Typography>
                </CardContent>
              </Card>
            </Zoom>
          </Grid>
          <Grid item xs={12} sm={6} md={4}>
            <Zoom in timeout={500}>
              <Card sx={{ borderRadius: 3, bgcolor: 'linear-gradient(135deg, #fff3e0 0%, #ffe0b2 100%)', borderLeft: '4px solid #f59e0b' }}>
                <CardContent>
                  <Typography variant="body2" sx={{ color: '#92400e', fontWeight: 500 }}>Lowest Salary</Typography>
                  <Typography variant="h5" fontWeight="bold" sx={{ color: '#f59e0b', mt: 0.5 }}>{formatCurrency(minSalary)}</Typography>
                  <Typography variant="caption" sx={{ color: '#92400e' }}>📊 Base salary</Typography>
                </CardContent>
              </Card>
            </Zoom>
          </Grid>
          <Grid item xs={12} sm={6} md={4}>
            <Zoom in timeout={600}>
              <Card sx={{ borderRadius: 3, bgcolor: 'linear-gradient(135deg, #e0f2fe 0%, #bae6fd 100%)', borderLeft: '4px solid #3b82f6' }}>
                <CardContent>
                  <Typography variant="body2" sx={{ color: '#075985', fontWeight: 500 }}>Salary Range</Typography>
                  <Typography variant="h5" fontWeight="bold" sx={{ color: '#3b82f6', mt: 0.5 }}>{formatCurrency(maxSalary - minSalary)}</Typography>
                  <Typography variant="caption" sx={{ color: '#075985' }}>📈 Gap between highest & lowest</Typography>
                </CardContent>
              </Card>
            </Zoom>
          </Grid>
        </Grid>

        {/* Charts Section */}
        <Grid container spacing={3} sx={{ mb: 4 }}>
          <Grid item xs={12} md={6}>
            <Fade in timeout={300}>
              <Paper sx={{ p: 3, borderRadius: 4, boxShadow: '0 2px 12px rgba(0,0,0,0.05)' }}>
                <Typography variant="h6" fontWeight="bold" gutterBottom sx={{ mb: 3, display: 'flex', alignItems: 'center', gap: 1 }}>
                  <BarChartIcon sx={{ color: '#6366f1' }} /> Payroll Distribution by Department
                </Typography>
                <ResponsiveContainer width="100%" height={320}>
                  <BarChart data={departmentStats} margin={{ top: 20, right: 30, left: 20, bottom: 60 }}>
                    <CartesianGrid strokeDasharray="3 3" stroke="#e2e8f0" />
                    <XAxis dataKey="department" angle={-45} textAnchor="end" height={70} tick={{ fontSize: 11 }} />
                    <YAxis tickFormatter={(value) => formatCurrency(value)} />
                    <RechartsTooltip formatter={(value) => formatCurrency(value)} />
                    <Bar dataKey="total_salary" fill="#6366f1" name="Total Salary" radius={[8, 8, 0, 0]} />
                  </BarChart>
                </ResponsiveContainer>
              </Paper>
            </Fade>
          </Grid>
          <Grid item xs={12} md={6}>
            <Fade in timeout={400}>
              <Paper sx={{ p: 3, borderRadius: 4, boxShadow: '0 2px 12px rgba(0,0,0,0.05)' }}>
                <Typography variant="h6" fontWeight="bold" gutterBottom sx={{ mb: 3, display: 'flex', alignItems: 'center', gap: 1 }}>
                  <PeopleIcon sx={{ color: '#f59e0b' }} /> Staff Count by Department
                </Typography>
                <ResponsiveContainer width="100%" height={320}>
                  <BarChart data={departmentStats} margin={{ top: 20, right: 30, left: 20, bottom: 60 }}>
                    <CartesianGrid strokeDasharray="3 3" stroke="#e2e8f0" />
                    <XAxis dataKey="department" angle={-45} textAnchor="end" height={70} tick={{ fontSize: 11 }} />
                    <YAxis />
                    <RechartsTooltip />
                    <Bar dataKey="staff_count" fill="#f59e0b" name="Staff Count" radius={[8, 8, 0, 0]} />
                  </BarChart>
                </ResponsiveContainer>
              </Paper>
            </Fade>
          </Grid>
        </Grid>

        <Grid container spacing={3} sx={{ mb: 4 }}>
          <Grid item xs={12} md={6}>
            <Fade in timeout={500}>
              <Paper sx={{ p: 3, borderRadius: 4, boxShadow: '0 2px 12px rgba(0,0,0,0.05)' }}>
                <Typography variant="h6" fontWeight="bold" gutterBottom sx={{ mb: 3, display: 'flex', alignItems: 'center', gap: 1 }}>
                  <PieChartIcon sx={{ color: '#10b981' }} /> Salary Distribution Percentage
                </Typography>
                <ResponsiveContainer width="100%" height={320}>
                  <PieChart>
                    <Pie 
                      data={pieChartData} 
                      cx="50%" 
                      cy="50%" 
                      innerRadius={60} 
                      outerRadius={100} 
                      dataKey="value" 
                      paddingAngle={3}
                      label={({ name, percent }) => `${name}: ${(percent * 100).toFixed(0)}%`}
                      labelLine={false}
                    >
                      {pieChartData.map((entry, index) => (<Cell key={`cell-${index}`} fill={COLORS[index % COLORS.length]} stroke="white" strokeWidth={2} />))}
                    </Pie>
                    <RechartsTooltip formatter={(value) => formatCurrency(value)} />
                    <Legend />
                  </PieChart>
                </ResponsiveContainer>
              </Paper>
            </Fade>
          </Grid>
          <Grid item xs={12} md={6}>
            <Fade in timeout={600}>
              <Paper sx={{ p: 3, borderRadius: 4, boxShadow: '0 2px 12px rgba(0,0,0,0.05)' }}>
                <Typography variant="h6" fontWeight="bold" gutterBottom sx={{ mb: 3, display: 'flex', alignItems: 'center', gap: 1 }}>
                  <SavingsIcon sx={{ color: '#ef4444' }} /> Salary Band Distribution
                </Typography>
                <ResponsiveContainer width="100%" height={280}>
                  <BarChart data={salaryBands} margin={{ top: 20, right: 30, left: 20, bottom: 50 }}>
                    <CartesianGrid strokeDasharray="3 3" stroke="#e2e8f0" />
                    <XAxis dataKey="label" angle={-45} textAnchor="end" height={60} tick={{ fontSize: 11 }} />
                    <YAxis />
                    <RechartsTooltip />
                    <Bar dataKey="count" name="Employees" radius={[8, 8, 0, 0]}>
                      {salaryBands.map((entry, index) => (<Cell key={`cell-${index}`} fill={entry.color} />))}
                    </Bar>
                  </BarChart>
                </ResponsiveContainer>
                <Typography variant="caption" sx={{ color: 'text.secondary', mt: 2, display: 'block', textAlign: 'center' }}>
                  Number of employees per salary bracket
                </Typography>
              </Paper>
            </Fade>
          </Grid>
        </Grid>

        {/* Department Statistics Table */}
        <Fade in timeout={700}>
          <Paper sx={{ p: 3, mb: 4, borderRadius: 4, boxShadow: '0 2px 12px rgba(0,0,0,0.05)' }}>
            <Typography variant="h6" fontWeight="bold" gutterBottom sx={{ mb: 3, display: 'flex', alignItems: 'center', gap: 1 }}>
              <AssessmentIcon sx={{ color: '#8b5cf6' }} /> Department Financial Summary
            </Typography>
            <TableContainer>
              <Table>
                <TableHead>
                  <TableRow sx={{ bgcolor: '#f1f5f9', borderRadius: 2 }}>
                    <TableCell sx={{ fontWeight: 'bold' }}>Department</TableCell>
                    <TableCell align="right" sx={{ fontWeight: 'bold' }}>Staff Count</TableCell>
                    <TableCell align="right" sx={{ fontWeight: 'bold' }}>Total Salary</TableCell>
                    <TableCell align="right" sx={{ fontWeight: 'bold' }}>Average Salary</TableCell>
                    <TableCell align="right" sx={{ fontWeight: 'bold' }}>% of Payroll</TableCell>
                  </TableRow>
                </TableHead>
                <TableBody>
                  {departmentStats.length === 0 ? (
                    <TableRow>
                      <TableCell colSpan={5} align="center" sx={{ py: 4 }}>
                        <Typography color="textSecondary">No department data available</Typography>
                      </TableCell>
                    </TableRow>
                  ) : (
                    departmentStats.map((dept, idx) => (
                      <TableRow key={idx} hover sx={{ '&:hover': { bgcolor: '#f8fafc' } }}>
                        <TableCell sx={{ fontWeight: 500 }}>{dept.department}</TableCell>
                        <TableCell align="right">
                          <Chip size="small" label={formatNumber(dept.staff_count)} sx={{ bgcolor: '#e0e7ff', color: '#4f46e5' }} />
                        </TableCell>
                        <TableCell align="right">{formatCurrency(dept.total_salary)}</TableCell>
                        <TableCell align="right">{formatCurrency(dept.avg_salary)}</TableCell>
                        <TableCell align="right">
                          <Chip 
                            label={`${totalPayroll > 0 ? ((dept.total_salary / totalPayroll) * 100).toFixed(1) : 0}%`}
                            size="small"
                            sx={{ bgcolor: alpha('#6366f1', 0.1), color: '#6366f1', fontWeight: 500 }}
                          />
                        </TableCell>
                      </TableRow>
                    ))
                  )}
                </TableBody>
              </Table>
            </TableContainer>
          </Paper>
        </Fade>

        {/* Staff Salary Table - FIXED with better height and pagination */}
        <Fade in timeout={800}>
          <Paper sx={{ borderRadius: 4, overflow: 'hidden', boxShadow: '0 2px 12px rgba(0,0,0,0.05)' }}>
            <Box sx={{ p: 2.5, bgcolor: '#f8fafc', borderBottom: '1px solid #e2e8f0' }}>
              <Typography variant="h6" fontWeight="bold">Staff Salary Register</Typography>
            </Box>
            
            {/* Filters */}
            <Box sx={{ p: 2.5, display: 'flex', gap: 2, flexWrap: 'wrap', alignItems: 'center', borderBottom: '1px solid #e2e8f0', bgcolor: 'white' }}>
              <TextField
                size="small"
                placeholder="Search by name, department, or phone..."
                value={searchTerm}
                onChange={(e) => {
                  setSearchTerm(e.target.value);
                  setPage(0);
                }}
                sx={{ flex: 2, minWidth: 200 }}
                InputProps={{ 
                  startAdornment: <InputAdornment position="start"><SearchIcon sx={{ color: '#94a3b8' }} /></InputAdornment>,
                  sx: { borderRadius: 2, bgcolor: '#f8fafc' }
                }}
              />
              <FormControl size="small" sx={{ minWidth: 180 }}>
                <InputLabel>Department</InputLabel>
                <Select 
                  value={departmentFilter} 
                  onChange={(e) => {
                    setDepartmentFilter(e.target.value);
                    setPage(0);
                  }} 
                  label="Department"
                  sx={{ borderRadius: 2, bgcolor: '#f8fafc' }}
                >
                  {uniqueDepartments.map((dept, idx) => (
                    <MenuItem key={idx} value={dept}>{dept === 'all' ? 'All Departments' : dept}</MenuItem>
                  ))}
                </Select>
              </FormControl>
              <FormControl size="small" sx={{ minWidth: 160 }}>
                <InputLabel>Sort By</InputLabel>
                <Select value={sortBy} onChange={(e) => setSortBy(e.target.value)} label="Sort By" sx={{ borderRadius: 2, bgcolor: '#f8fafc' }}>
                  <MenuItem value="salary_desc">Highest Salary First</MenuItem>
                  <MenuItem value="salary_asc">Lowest Salary First</MenuItem>
                  <MenuItem value="name_asc">Name (A-Z)</MenuItem>
                  <MenuItem value="name_desc">Name (Z-A)</MenuItem>
                </Select>
              </FormControl>
              <Box sx={{ ml: 'auto' }}>
                <Chip 
                  label={`Total: ${formatNumber(filteredSalaries.length)} staff | Monthly: ${formatCurrency(filteredSalaries.reduce((sum, s) => sum + (Number(s.salary_amount) || 0), 0))}`}
                  size="small"
                  sx={{ bgcolor: '#e0e7ff', color: '#4f46e5', fontWeight: 500 }}
                />
              </Box>
            </Box>

            {/* Table with improved height - FIXED */}
            <TableContainer sx={{ 
              maxHeight: 'calc(100vh - 420px)', 
              overflow: 'auto',
              '&::-webkit-scrollbar': {
                width: 8,
                height: 8,
              },
              '&::-webkit-scrollbar-track': {
                background: '#f1f5f9',
                borderRadius: 4,
              },
              '&::-webkit-scrollbar-thumb': {
                background: '#cbd5e1',
                borderRadius: 4,
                '&:hover': {
                  background: '#94a3b8',
                },
              },
            }}>
              <Table stickyHeader size="medium">
                <TableHead>
                  <TableRow sx={{ bgcolor: '#f8fafc' }}>
                    <TableCell sx={{ fontWeight: 'bold', py: 1.5 }}>#</TableCell>
                    <TableCell sx={{ fontWeight: 'bold', py: 1.5 }}>Staff Name</TableCell>
                    <TableCell sx={{ fontWeight: 'bold', py: 1.5 }}>Department</TableCell>
                    <TableCell sx={{ fontWeight: 'bold', py: 1.5 }}>Phone</TableCell>
                    <TableCell align="right" sx={{ fontWeight: 'bold', py: 1.5 }}>Salary Amount</TableCell>
                    <TableCell sx={{ fontWeight: 'bold', py: 1.5 }}>Bank</TableCell>
                    <TableCell sx={{ fontWeight: 'bold', py: 1.5 }}>Account Number</TableCell>
                    <TableCell align="center" sx={{ fontWeight: 'bold', py: 1.5 }}>Actions</TableCell>
                  </TableRow>
                </TableHead>
                <TableBody>
                  {paginatedSalaries.length === 0 ? (
                    <TableRow>
                      <TableCell colSpan={8} align="center" sx={{ py: 5 }}>
                        <Typography variant="body1" color="textSecondary">No salary records found for active staff</Typography>
                      </TableCell>
                    </TableRow>
                  ) : (
                    paginatedSalaries.map((staff, index) => (
                      <TableRow key={staff.id} hover sx={{ '&:hover': { bgcolor: '#f8fafc' } }}>
                        <TableCell sx={{ py: 1.5 }}>{page * rowsPerPage + index + 1}</TableCell>
                        <TableCell sx={{ py: 1.5 }}>
                          <Box sx={{ display: 'flex', alignItems: 'center', gap: 2 }}>
                            <Avatar sx={{ bgcolor: '#6366f1', width: 36, height: 36 }}>
                              <PersonIcon sx={{ fontSize: 18 }} />
                            </Avatar>
                            <Box>
                              <Typography variant="body2" fontWeight="medium">{staff.full_name || 'N/A'}</Typography>
                              <Typography variant="caption" sx={{ color: 'text.secondary' }}>{staff.email || 'N/A'}</Typography>
                            </Box>
                          </Box>
                        </TableCell>
                        <TableCell sx={{ py: 1.5 }}>
                          <Chip size="small" label={staff.department || 'N/A'} variant="outlined" sx={{ borderRadius: 1.5 }} />
                        </TableCell>
                        <TableCell sx={{ py: 1.5 }}>
                          <Typography variant="body2">{staff.phone || 'N/A'}</Typography>
                        </TableCell>
                        <TableCell align="right" sx={{ py: 1.5 }}>
                          <Typography variant="body2" fontWeight="bold" sx={{ color: '#6366f1' }}>
                            {formatCurrency(staff.salary_amount)}
                          </Typography>
                        </TableCell>
                        <TableCell sx={{ py: 1.5 }}>
                          <Typography variant="body2">{staff.bank_name || 'Not set'}</Typography>
                        </TableCell>
                        <TableCell sx={{ py: 1.5 }}>
                          <Typography variant="body2" fontFamily="monospace">{staff.account_number || 'Not set'}</Typography>
                        </TableCell>
                        <TableCell align="center" sx={{ py: 1.5 }}>
                          <Tooltip title="View Details">
                            <IconButton size="small" onClick={() => handleViewStaff(staff)} sx={{ color: '#6366f1' }}>
                              <VisibilityIcon fontSize="small" />
                            </IconButton>
                          </Tooltip>
                          <Tooltip title="Salary History">
                            <IconButton size="small" onClick={() => handleViewHistory(staff)} sx={{ color: '#f59e0b' }}>
                              <HistoryIcon fontSize="small" />
                            </IconButton>
                          </Tooltip>
                        </TableCell>
                      </TableRow>
                    ))
                  )}
                </TableBody>
              </Table>
            </TableContainer>
            
            {/* Pagination */}
            <Box sx={{ borderTop: '1px solid #e2e8f0', bgcolor: 'white' }}>
              <TablePagination
                component="div"
                count={filteredSalaries.length}
                page={page}
                onPageChange={(e, newPage) => setPage(newPage)}
                rowsPerPage={rowsPerPage}
                onRowsPerPageChange={(e) => {
                  setRowsPerPage(parseInt(e.target.value, 10));
                  setPage(0);
                }}
                rowsPerPageOptions={[10, 15, 25, 50, 100]}
                labelRowsPerPage="Rows per page:"
                sx={{
                  '.MuiTablePagination-select': { borderRadius: 1 },
                  '.MuiTablePagination-displayedRows': { fontSize: '0.875rem' }
                }}
              />
            </Box>
          </Paper>
        </Fade>

        {/* Footer */}
        <Box sx={{ textAlign: 'center', py: 3, mt: 2 }}>
          <Typography variant="body2" sx={{ color: 'text.secondary' }}>
            © {new Date().getFullYear()} Elegantè IT - Accountant Dashboard | Payroll Management System
          </Typography>
        </Box>
      </Box>

      {/* View Staff Dialog */}
      <Dialog open={viewDialogOpen} onClose={() => setViewDialogOpen(false)} maxWidth="md" fullWidth>
        {selectedStaff && (
          <>
            <DialogTitle sx={{ bgcolor: '#4f46e5', color: 'white', pb: 2 }}>
              <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
                <Typography variant="h6" fontWeight="bold">Staff Financial Details</Typography>
                <IconButton onClick={() => setViewDialogOpen(false)} sx={{ color: 'white' }}>
                  <CloseIcon />
                </IconButton>
              </Box>
            </DialogTitle>
            <DialogContent sx={{ pt: 3 }}>
              <Grid container spacing={3}>
                <Grid item xs={12} md={6}>
                  <Paper sx={{ p: 2.5, bgcolor: '#f8fafc', borderRadius: 3 }}>
                    <Typography variant="subtitle2" sx={{ color: '#6366f1', fontWeight: 600, mb: 2 }}>Personal Information</Typography>
                    <Stack spacing={1.5}>
                      <Box><strong>Name:</strong> {selectedStaff.full_name}</Box>
                      <Box><strong>Department:</strong> {selectedStaff.department}</Box>
                      <Box><strong>Phone:</strong> {selectedStaff.phone}</Box>
                      <Box><strong>Email:</strong> {selectedStaff.email}</Box>
                    </Stack>
                  </Paper>
                </Grid>
                <Grid item xs={12} md={6}>
                  <Paper sx={{ p: 2.5, bgcolor: '#f8fafc', borderRadius: 3 }}>
                    <Typography variant="subtitle2" sx={{ color: '#f59e0b', fontWeight: 600, mb: 2 }}>Banking Information</Typography>
                    <Stack spacing={1.5}>
                      <Box><strong>Bank:</strong> {selectedStaff.bank_name || 'Not set'}</Box>
                      <Box><strong>Account Number:</strong> {selectedStaff.account_number || 'Not set'}</Box>
                      <Box><strong>Account Name:</strong> {selectedStaff.account_name || 'Not set'}</Box>
                      <Divider />
                      <Box><strong>Current Salary:</strong> <span style={{ fontWeight: 'bold', color: '#4f46e5' }}>{formatCurrency(selectedStaff.salary_amount)}</span></Box>
                    </Stack>
                  </Paper>
                </Grid>
              </Grid>
            </DialogContent>
            <DialogActions sx={{ p: 2.5 }}>
              <Button onClick={() => setViewDialogOpen(false)} variant="outlined" sx={{ borderRadius: 2, textTransform: 'none' }}>Close</Button>
            </DialogActions>
          </>
        )}
      </Dialog>

      {/* Salary History Dialog */}
      <Dialog open={historyDialogOpen} onClose={() => setHistoryDialogOpen(false)} maxWidth="lg" fullWidth>
        {selectedStaff && (
          <>
            <DialogTitle sx={{ bgcolor: '#f59e0b', color: 'white', pb: 2 }}>
              <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
                <Typography variant="h6" fontWeight="bold">Salary History - {selectedStaff.full_name}</Typography>
                <IconButton onClick={() => setHistoryDialogOpen(false)} sx={{ color: 'white' }}>
                  <CloseIcon />
                </IconButton>
              </Box>
            </DialogTitle>
            <DialogContent sx={{ pt: 3 }}>
              {salaryHistory.length === 0 ? (
                <Alert severity="info" sx={{ borderRadius: 2 }}>
                  No salary adjustment records found for this employee.
                </Alert>
              ) : (
                <TableContainer component={Paper} sx={{ borderRadius: 2, overflow: 'hidden' }}>
                  <Table size="medium">
                    <TableHead sx={{ bgcolor: '#f8fafc' }}>
                      <TableRow>
                        <TableCell sx={{ fontWeight: 'bold' }}>Date</TableCell>
                        <TableCell align="right" sx={{ fontWeight: 'bold' }}>Previous Salary</TableCell>
                        <TableCell align="right" sx={{ fontWeight: 'bold' }}>New Salary</TableCell>
                        <TableCell align="right" sx={{ fontWeight: 'bold' }}>Change</TableCell>
                        <TableCell sx={{ fontWeight: 'bold' }}>Type</TableCell>
                        <TableCell sx={{ fontWeight: 'bold' }}>Status</TableCell>
                        <TableCell sx={{ fontWeight: 'bold' }}>Reason</TableCell>
                      </TableRow>
                    </TableHead>
                    <TableBody>
                      {salaryHistory.map((record, idx) => (
                        <TableRow key={idx} hover>
                          <TableCell>{record.created_at ? new Date(record.created_at).toLocaleDateString() : 'N/A'}</TableCell>
                          <TableCell align="right">{formatCurrency(record.previous_salary)}</TableCell>
                          <TableCell align="right"><strong>{formatCurrency(record.new_salary)}</strong></TableCell>
                          <TableCell align="right">
                            <Chip 
                              size="small"
                              label={`${record.adjustment_type === 'increment' ? '+' : '-'}${formatCurrency(record.increment_amount)}`}
                              sx={{ bgcolor: record.adjustment_type === 'increment' ? alpha('#10b981', 0.1) : alpha('#ef4444', 0.1), color: record.adjustment_type === 'increment' ? '#10b981' : '#ef4444' }}
                            />
                          </TableCell>
                          <TableCell>
                            <Chip 
                              size="small" 
                              label={record.adjustment_type === 'increment' ? 'Increment' : 'Downgrade'}
                              sx={{ bgcolor: record.adjustment_type === 'increment' ? alpha('#10b981', 0.1) : alpha('#ef4444', 0.1), color: record.adjustment_type === 'increment' ? '#10b981' : '#ef4444' }}
                            />
                          </TableCell>
                          <TableCell>
                            <Chip 
                              size="small" 
                              label={record.status === 'approved' ? 'Approved' : 'Pending'}
                              sx={{ bgcolor: record.status === 'approved' ? alpha('#10b981', 0.1) : alpha('#f59e0b', 0.1), color: record.status === 'approved' ? '#10b981' : '#f59e0b' }}
                            />
                          </TableCell>
                          <TableCell>
                            <Typography variant="body2" sx={{ maxWidth: 250 }}>
                              {record.reason || 'N/A'}
                            </Typography>
                          </TableCell>
                        </TableRow>
                      ))}
                    </TableBody>
                  </Table>
                </TableContainer>
              )}
            </DialogContent>
            <DialogActions sx={{ p: 2.5 }}>
              <Button onClick={() => setHistoryDialogOpen(false)} variant="outlined" sx={{ borderRadius: 2, textTransform: 'none' }}>Close</Button>
            </DialogActions>
          </>
        )}
      </Dialog>

      <Snackbar 
        open={snackbar.open} 
        autoHideDuration={4000} 
        onClose={() => setSnackbar({ ...snackbar, open: false })} 
        anchorOrigin={{ vertical: 'bottom', horizontal: 'right' }}
      >
        <Alert 
          severity={snackbar.severity} 
          onClose={() => setSnackbar({ ...snackbar, open: false })}
          variant="filled"
          sx={{ borderRadius: 2 }}
        >
          {snackbar.message}
        </Alert>
      </Snackbar>
    </Box>
  );
};

export default AccountantSalaryDashboard;